// Imports ------------------------------------------------------------------->

import {inject} from "../di";
import {ConfigService} from "../ConfigService";
import {IAuthenticationService, AuthUser, AuthProviderUser, AuthStatus} from "../definitions/IAuthenticationService";
import { AccountsUser, AuthProvider } from "../../definitions/CommonDefinitions";
import { fetch, FetchOptions } from "../../utils/fetch";
import { ConfirmEmailData, UserData, UserResponse } from "../AuthenticationService";
import { LocationService } from "../LocationService";

type EmailLoginResponse = UserResponse & {password_requirements: string};

export class EmailAuthenticator implements IAuthenticationService {

	@inject config: ConfigService;
	@inject locationService: LocationService;

	private userInfo: AuthProviderUser;
	private token: AccountsUser;
	private loginPromise: Promise<void>;
	private registerPromise: Promise<void>;
	private finishRegistrationPromise: Promise<void>;

	get status() {
		return this.userInfo ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT;
	}

	get user() {
		return {
			info: this.userInfo,
			token: this.token,
		} as AuthUser;
	}

	init = () => {
		return new Promise<void>((resolve, reject) => {
			const userCookie = Cookies.get("flipp-user");
			if (userCookie) {
				const data = JSON.parse(userCookie);
				this.userInfo = data;
			}
			resolve();
		});
	}

	register = (body: UserData) => {
		this.registerPromise = new Promise((resolve, reject) => {
			this.makeRequest("signup", body).then((res) => {
				Cookies.set("flipp-unverified-user", {
					...res.user,
					email: body.email,
				}, {
					expires: 30,
					domain: this.config.ENV === "development" ? "localhost" : ".flipp.com"
				});
				resolve();
			}).catch((e: Error) => {
				reject(e);
			});
		});

		return this.registerPromise;
	}

	finishRegistration = (body: ConfirmEmailData) => {
		// const {token, account_guid} = ANONYMOUS_USER_CREDENTIALS;
		this.finishRegistrationPromise = new Promise((resolve, reject) => {
			this.makeRequest("confirm", body).then((res) => {
				this.userInfo = this.getUserInfo(res);
				Cookies.set("flipp-user", JSON.stringify(this.userInfo), {
					expires: 30,
					domain: this.config.ENV === "development" ? "localhost" : ".flipp.com"
				});
				this.token = this.getUserToken(res);
				resolve();
			}).catch((e: Error) => {
				reject(e);
			});
		});
		return this.finishRegistrationPromise;
	}

	login = (source: AuthProvider, target?: EventTarget, body?: UserData) => {
		this.loginPromise = new Promise((resolve, reject) => {
			
			this.makeRequest("signin", body).then((res) => {
				this.userInfo = this.getUserInfo(res);
				Cookies.set("flipp-user", JSON.stringify(this.userInfo), {
					expires: 30,
					domain: this.config.ENV === "development" ? "localhost" : ".flipp.com"
				});
				this.token = this.getUserToken(res);
				resolve();
			}).catch((e: Error) => {
				this._resetLogin();
				reject(e);
			});

		});

		return this.loginPromise;
	}

	logout = () => {
		return Promise.resolve().then(() => {
			this.userInfo = null;
			Cookies.remove("flipp-user", { domain: this.config.ENV === "development" ? "localhost" : ".flipp.com" });
		});
	}
	
	makeRequest( uri: string, body?: any, accessToken?: string, method = "POST") {
		const heads = [
			{"Content-Type": "application/json"},
		];
		const headers = accessToken ? [...heads, {"Authorization": `Token token=${accessToken}`}] : heads;
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/email/${uri}`,
			method,
			headers,
		};
		const options = body ? {...opts, body: JSON.stringify(this.extendWithBaseParams(body))} : opts;
		return fetch<EmailLoginResponse>(options, [200], 0).then(res => {
			if (res.user) {
				this.userInfo = this.getUserInfo(res);
				Cookies.set("flipp-user", JSON.stringify(this.userInfo), {
					expires: 30,
					domain: this.config.ENV === "development" ? "localhost" : ".flipp.com"
				});
				this.token = this.getUserToken(res);
				Cookies.set("flipp-login", JSON.stringify({token: this.token}), {
					expires: 30,
					domain: this.config.ENV === "development" ? "localhost" : ".flipp.com"
				});
			}
			return res;
		});
	}
	
	private getUserInfo(res: UserResponse) {
		return  {
			id: res.user.id,
			image:  "",
			email: res.email,
			accessToken: res.user.api_access_token,
			expiresAt: new Date().getTime(),
			provider: AuthProvider.EMAIL
		};
	}
	
	private getUserToken(res: UserResponse) {
		return {
			user_id: res.user.id,
			subscribed: res.user.subscribed,
			email: res.email,
			email_subscribed: res.user.email_subscribed,
			access_token: res.user.api_access_token,
			third_party_data: res.third_party_data,
		};
	}

	private extendWithBaseParams(body: any) {
		return {...body, locale: this.locationService.lang};
	}

	private _resetLogin = () => {
		this.loginPromise = null;
	}
}
